import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { productByKey } from "@/lib/products";

export default function UsageMeter({ product, refreshKey = 0, className = "" }) {
  const [usage, setUsage] = useState(null);
  const nav = useNavigate();
  const p = productByKey(product);

  useEffect(() => {
    let alive = true;
    api.get("/billing/usage").then((r) => {
      if (!alive) return;
      const u = r.data.find((x) => x.product === product);
      setUsage(u || null);
    }).catch(() => {});
    return () => { alive = false; };
  }, [product, refreshKey]);

  if (!usage || !p) return null;

  // No active plan — show "Subscribe" affordance (especially relevant for Sufi which has no free tier)
  if (!usage.tier) {
    return (
      <div
        data-testid={`usage-meter-${product}`}
        className={`rounded-sm border border-border/60 bg-card px-3 py-2 text-xs ${className}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 min-w-0 text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            <span className="uppercase tracking-[0.16em]">No plan</span>
          </div>
          <button
            data-testid={`usage-upgrade-${product}`}
            onClick={() => nav("/app/billing")}
            className="inline-flex items-center gap-1 font-medium hover:opacity-80 transition-opacity"
            style={{ color: p.accent }}
          >
            Subscribe
            <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
        <div className="mt-1 text-muted-foreground text-[11px]">First call activates free tier · or upgrade now.</div>
      </div>
    );
  }

  const isUnlimited = usage.limit === -1;
  const pct = isUnlimited ? 0 : Math.min(100, (usage.used / Math.max(1, usage.limit)) * 100);
  const danger = !isUnlimited && pct >= 80;
  const exhausted = !isUnlimited && usage.remaining === 0;

  return (
    <div
      data-testid={`usage-meter-${product}`}
      className={`rounded-sm border border-border/60 bg-card px-3 py-2 text-xs ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="uppercase tracking-[0.16em] text-muted-foreground">{usage.plan_name}</span>
        </div>
        <button
          data-testid={`usage-upgrade-${product}`}
          onClick={() => nav("/app/billing")}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          {exhausted ? "Upgrade" : "Manage"}
          <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="font-mono text-foreground" style={{ color: danger ? "#ef4444" : undefined }}>
          {usage.used.toLocaleString()}
        </span>
        <span className="text-muted-foreground">/ {isUnlimited ? "∞" : usage.limit.toLocaleString()}</span>
        <span className="text-muted-foreground hidden sm:inline">
          {usage.period_end ? `until ${new Date(usage.period_end).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "this period"}
        </span>
      </div>
      {!isUnlimited && (
        <div className="mt-1.5 h-1 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${pct}%`,
              background: exhausted ? "#ef4444" : p.accent,
            }}
          />
        </div>
      )}
    </div>
  );
}
